import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import HreflangTags from "@/components/seo/HreflangTags";
import HreflangFrEn from "@/components/seo/HreflangFrEn";
import Navbar from "@/components/landing/Navbar";
import HeroSection from "@/components/landing/HeroSection";
import AdvantagesSection from "@/components/landing/AdvantagesSection";
import LazyMount from "@/components/LazyMount";
import { faqJsonLd, homepageFaq } from "@/lib/seo-jsonld";

// Per-section code-splitting so each chunk loads independently
// only when it approaches the viewport (better TBT/LCP on mobile).
const NetworkMapSection = lazy(() => import("@/components/landing/NetworkMapSection"));
const ProcessSection = lazy(() => import("@/components/landing/ProcessSection"));
const SocialProofSection = lazy(() => import("@/components/landing/SocialProofSection"));
const ExperiencesSection = lazy(() => import("@/components/landing/ExperiencesSection"));
const ZonesDesserviesSection = lazy(() => import("@/components/landing/ZonesDesserviesSection"));
const FaqSection = lazy(() => import("@/components/landing/FaqSection"));
const Footer = lazy(() => import("@/components/landing/Footer"));

const sectionFallback = <div className="min-h-[400px]" aria-hidden="true" />;

const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "eBike & Co",
  url: "https://e-bikeandco.com",
  logo: "https://e-bikeandco.com/favicon.avif",
  image: "https://e-bikeandco.com/images/ebikeandco-location-velo-landes-17.jpg",

  description: "Location de vélos électriques et classiques dans les Landes. 8 points, livraison à votre hébergement, assistance 7j/7.",
  telephone: "+33558427512",
  email: "info@e-bikeandco.com",
  areaServed: [
    { "@type": "AdministrativeArea", name: "Landes (40), France" },
    { "@type": "Place", name: "Côte landaise" },
    ...["Mimizan", "Bias", "Lit-et-Mixe", "Mézos", "Saint-Julien-en-Born", "Contis Plage", "Vielle-Saint-Girons", "Léon", "Moliets-et-Maa", "Azur", "Castets", "Lévignacq", "Taller", "Saint-Michel-Escalus", "Onesse-Laharie"].map((c) => ({ "@type": "City", name: c })),
  ],
  priceRange: "€€",
  address: {
    "@type": "PostalAddress",
    streetAddress: "208 Rte des Lacs",
    addressLocality: "Saint-Julien-en-Born",
    postalCode: "40170",
    addressRegion: "Landes",
    addressCountry: "FR",
  },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    opens: "09:00",
    closes: "19:00",
  },
  sameAs: [
    "https://www.instagram.com/ebikeandco",
    "https://www.facebook.com/ebikeandco",
  ],
};

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>eBike & Co — Location de vélos dans les Landes</title>
        <meta name="description" content="Louez vos vélos électriques et classiques dans les Landes avec eBike & Co. 8 points, livraison à votre hébergement. Réservez en ligne." />
        <link rel="canonical" href="https://e-bikeandco.com/" />
        <meta property="og:title" content="eBike & Co — Location de vélos dans les Landes" />
        <meta property="og:description" content="8 points de location, livraison à votre hébergement, flotte récente. Réservez en ligne." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://e-bikeandco.com/" />
        <meta property="og:locale" content="fr_FR" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(homepageFaq))}</script>
      </Helmet>
      <HreflangTags />
      <HreflangFrEn frPath="/" />
      <Navbar />
      <main className="pt-16">
        <HeroSection />
        <AdvantagesSection />
        <LazyMount minHeight={620} rootMargin="400px">
          <Suspense fallback={sectionFallback}>
            <NetworkMapSection />
          </Suspense>
        </LazyMount>
        <LazyMount minHeight={500}>
          <Suspense fallback={sectionFallback}>
            <ProcessSection />
          </Suspense>
        </LazyMount>
        <LazyMount minHeight={400}>
          <Suspense fallback={sectionFallback}>
            <SocialProofSection />
          </Suspense>
        </LazyMount>
        <LazyMount minHeight={500}>
          <Suspense fallback={sectionFallback}>
            <ExperiencesSection />
          </Suspense>
        </LazyMount>
        <LazyMount minHeight={400}>
          <Suspense fallback={sectionFallback}>
            <ZonesDesserviesSection />
          </Suspense>
        </LazyMount>
        <LazyMount minHeight={400}>
          <Suspense fallback={sectionFallback}>
            <FaqSection />
          </Suspense>
        </LazyMount>
        <LazyMount minHeight={300}>
          <Suspense fallback={sectionFallback}>
            <Footer />
          </Suspense>
        </LazyMount>
      </main>
    </div>
  );
};

export default Index;
